import { CalendarClock, CircleDot, FolderOpen, Repeat, Signal, Tag, Type, UserPen, Users } from 'lucide-react'
import { getDuePresets } from './duePresets'
import type { FilterFieldDefinition, FilterFieldKey, FilterOperator } from '@/lib/types/filter'

export const FILTER_FIELD_DEFINITIONS: Record<FilterFieldKey, FilterFieldDefinition> = {
  status: {
    key: 'status',
    label: 'Status',
    pluralLabel: 'statuses',
    fieldType: 'select',
    icon: CircleDot,
    operators: ['includes', 'excludes'],
    defaultOperator: 'includes',
    getTaskValue: (task) => task.statusId,
  },
  priority: {
    key: 'priority',
    label: 'Priority',
    pluralLabel: 'priorities',
    fieldType: 'select',
    icon: Signal,
    operators: ['includes', 'excludes', 'at_least'],
    defaultOperator: 'includes',
    getTaskValue: (task) => task.priority ?? 0,
  },
  assignee: {
    key: 'assignee',
    label: 'Assignee',
    pluralLabel: 'assignees',
    fieldType: 'select',
    icon: Users,
    operators: ['includes', 'excludes'],
    defaultOperator: 'includes',
    getTaskValue: (task) => task.userId ?? '',
  },
  createdBy: {
    key: 'createdBy',
    label: 'Created by',
    pluralLabel: 'creators',
    fieldType: 'select',
    icon: UserPen,
    operators: ['includes', 'excludes'],
    defaultOperator: 'includes',
    getTaskValue: (task) => task.createdBy,
  },
  due: {
    key: 'due',
    label: 'Due',
    pluralLabel: 'due dates',
    fieldType: 'select',
    icon: CalendarClock,
    // is / is not take relative presets; before / on / after take a day
    // (see isDueDateOperator).
    operators: ['includes', 'excludes', 'before', 'on', 'after'],
    defaultOperator: 'includes',
    // Relative buckets, evaluated at filter time (see duePresets).
    getTaskValue: (task) => getDuePresets(task.dueDate),
  },
  labels: {
    key: 'labels',
    label: 'Labels',
    pluralLabel: 'labels',
    fieldType: 'select',
    icon: Tag,
    operators: ['includes', 'excludes'],
    defaultOperator: 'includes',
    getTaskValue: () => null,
    requiresJoinData: true,
  },
  name: {
    key: 'name',
    label: 'Title',
    menuLabel: 'Title contains…',
    pluralLabel: 'titles',
    fieldType: 'text',
    icon: Type,
    operators: ['contains', 'does_not_contain', 'equals', 'not_equal'],
    defaultOperator: 'contains',
    getTaskValue: (task) => task.name,
  },
  project: {
    key: 'project',
    label: 'Project',
    pluralLabel: 'projects',
    fieldType: 'select',
    icon: FolderOpen,
    operators: ['includes', 'excludes'],
    defaultOperator: 'includes',
    getTaskValue: (task) => task.projectId ?? '',
  },
  cycle: {
    key: 'cycle',
    label: 'Cycle',
    pluralLabel: 'cycles',
    fieldType: 'select',
    icon: Repeat,
    operators: ['includes', 'excludes'],
    defaultOperator: 'includes',
    getTaskValue: () => null,
    requiresJoinData: true,
  },
}

export const FILTER_FIELD_ORDER: Array<FilterFieldKey> = [
  'status',
  'priority',
  'assignee',
  'labels',
  'cycle',
  'due',
  'createdBy',
  'project',
  'name',
]

/** Due operators that compare against a day rather than a relative preset. */
export function isDueDateOperator(operator: FilterOperator): boolean {
  return operator === 'before' || operator === 'on' || operator === 'after'
}
